import { z } from 'zod';

const sessionResponse = z.object({
  accessToken: z.string().min(1),
  accessExpiresAt: z.iso.datetime(),
  refreshToken: z.string().min(1),
  refreshExpiresAt: z.iso.datetime(),
}).strict();
export type HelpdeskSessionResponse = z.infer<typeof sessionResponse>;

const storedSession = z.object({
  sessionId: z.uuid(),
  refreshToken: z.string().min(1),
  refreshExpiresAt: z.iso.datetime(),
}).strict();
type StoredSession = z.infer<typeof storedSession>;

export type WidgetSessionStatus = 'guest' | 'restoring' | 'connected' | 'reconnect';
export interface WidgetSessionSnapshot {
  status: WidgetSessionStatus;
  generation: number;
  reason?: 'expired' | 'network' | 'storage' | 'denied';
  notice?: 'revocation-unconfirmed' | 'storage-unavailable';
}

const SESSION_PREFIX = 'move.helpdesk.session.v1:';
const SUPPRESSION_PREFIX = 'move.helpdesk.suppress-auto-login.v1:';
const EXPIRY_SKEW_MS = 15_000;
const REFRESH_ATTEMPTS = 2;
const REFRESH_TIMEOUT_MS = 30_000;

function readStored(key: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? storedSession.parse(JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function isSuppressed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function isValidUntil(value: string): boolean {
  return Date.parse(value) > Date.now() + EXPIRY_SKEW_MS;
}

/** Host-origin Helpdesk credentials. Main Move credentials never enter this store. */
export class WidgetSession {
  private snapshotValue: WidgetSessionSnapshot;
  private listeners = new Set<() => void>();
  private access: Pick<HelpdeskSessionResponse, 'accessToken' | 'accessExpiresAt'> | null = null;
  private pending: Promise<string> | null = null;
  private disposed = false;
  private sessionId: string | null;
  private accountIntent: boolean;
  private suppressed: boolean;
  private readonly sessionKey: string;
  private readonly suppressionKey: string;
  private readonly apiOrigin: string;

  constructor(private readonly apiBase: string) {
    this.apiOrigin = new URL(apiBase).origin;
    this.sessionKey = SESSION_PREFIX + this.apiOrigin;
    this.suppressionKey = SUPPRESSION_PREFIX + this.apiOrigin;
    this.suppressed = isSuppressed(this.suppressionKey);
    const stored = this.suppressed ? null : readStored(this.sessionKey);
    this.sessionId = stored?.sessionId ?? null;
    this.accountIntent = stored !== null;
    this.snapshotValue = {
      status: stored ? 'restoring' : 'guest',
      generation: 0,
    };
    window.addEventListener('storage', this.onStorage);
    if (stored) void this.restore();
  }

  snapshot = (): WidgetSessionSnapshot => this.snapshotValue;

  hasAccountActor = (): boolean => this.accountIntent;
  isSuppressed = (): boolean => this.suppressed;
  canRetry = (): boolean => readStored(this.sessionKey) !== null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private update(status: WidgetSessionStatus, reason?: WidgetSessionSnapshot['reason'], actorChanged = false): void {
    this.snapshotValue = {
      status,
      generation: this.snapshotValue.generation + (actorChanged ? 1 : 0),
      ...(reason ? { reason } : {}),
    };
    for (const listener of this.listeners) listener();
  }

  private notice(value: WidgetSessionSnapshot['notice'], generation: number): void {
    if (this.disposed || this.snapshotValue.generation !== generation) return;
    this.snapshotValue = { ...this.snapshotValue, ...(value ? { notice: value } : {}) };
    for (const listener of this.listeners) listener();
  }

  private onStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== this.sessionKey && event.key !== this.suppressionKey) return;
    const stored = readStored(this.sessionKey);
    if (isSuppressed(this.suppressionKey)) {
      this.suppressed = true;
      this.sessionId = null;
      this.accountIntent = false;
      this.access = null;
      this.pending = null;
      this.update('guest', undefined, true);
      return;
    }
    this.suppressed = false;
    if (!stored) {
      this.access = null;
      this.pending = null;
      this.update(this.accountIntent ? 'reconnect' : 'guest', 'expired');
      return;
    }
    if (stored.sessionId !== this.sessionId) {
      this.sessionId = stored.sessionId;
      this.accountIntent = true;
      this.access = null;
      this.pending = null;
      this.update('restoring', undefined, true);
      void this.restore();
    }
  };

  private async restore(): Promise<void> {
    try {
      await this.getAccessToken();
    } catch {
      // getAccessToken sets the recoverable state.
    }
  }

  /** Accept only a response from a validated Move popup handshake. */
  accept(response: unknown): void {
    const issued = sessionResponse.parse(response);
    const sessionId = crypto.randomUUID();
    const record: StoredSession = {
      sessionId,
      refreshToken: issued.refreshToken,
      refreshExpiresAt: issued.refreshExpiresAt,
    };
    try {
      localStorage.setItem(this.sessionKey, JSON.stringify(record));
      localStorage.removeItem(this.suppressionKey);
    } catch {
      this.access = null;
      this.sessionId = sessionId;
      this.accountIntent = true;
      this.update('reconnect', 'storage', true);
      void fetch(new URL('/api/v1/helpdesk/sessions/logout', this.apiBase), {
        method: 'POST', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: issued.refreshToken }),
        signal: AbortSignal.timeout(10_000),
      }).catch(() => undefined);
      return;
    }
    this.sessionId = sessionId;
    this.accountIntent = true;
    this.suppressed = false;
    this.access = issued;
    this.pending = null;
    this.update('connected', undefined, true);
  }

  async getAccessToken(): Promise<string> {
    if (this.disposed) throw new Error('Helpdesk widget was removed.');
    if (this.access && isValidUntil(this.access.accessExpiresAt)) return this.access.accessToken;
    if (this.pending) return this.pending;
    const generation = this.snapshotValue.generation;
    const pending = this.refresh(generation);
    this.pending = pending;
    void pending.finally(() => { if (this.pending === pending) this.pending = null; }).catch(() => undefined);
    return pending;
  }

  private async refresh(generation: number): Promise<string> {
    const controller = new AbortController();
    const timeout = setTimeout(() => { controller.abort(); }, REFRESH_TIMEOUT_MS);
    const execute = async (): Promise<string> => {
      for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt += 1) {
        const record = readStored(this.sessionKey);
        if (!record || !isValidUntil(record.refreshExpiresAt) || record.sessionId !== this.sessionId)
          throw new Error('Helpdesk connection expired.');
        const response = await fetch(new URL('/api/v1/helpdesk/sessions/refresh', this.apiBase), {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: record.refreshToken }),
            signal: controller.signal,
          });
          if (!response.ok) {
            if (response.status === 401 && attempt + 1 < REFRESH_ATTEMPTS &&
                readStored(this.sessionKey)?.refreshToken !== record.refreshToken) continue;
            throw new Error(response.status === 401 ? 'Helpdesk connection expired.' : 'Helpdesk connection unavailable.');
          }
          const next = sessionResponse.parse(await response.json() as unknown);
          if (this.disposed || generation !== this.snapshotValue.generation ||
              readStored(this.sessionKey)?.sessionId !== record.sessionId) throw new Error('Helpdesk actor changed.');
          const updated: StoredSession = {
            sessionId: record.sessionId,
            refreshToken: next.refreshToken,
            refreshExpiresAt: record.refreshExpiresAt,
          };
          localStorage.setItem(this.sessionKey, JSON.stringify(updated));
          this.access = next;
          if (this.snapshotValue.status !== 'connected') this.update('connected');
          return next.accessToken;
      }
      throw new Error('Helpdesk connection unavailable.');
    };
    try {
      if (!('locks' in navigator)) throw new Error('This browser cannot coordinate Helpdesk renewal.');
      return await navigator.locks.request(`move-helpdesk-session-refresh:${this.apiOrigin}`,
        { signal: controller.signal }, execute);
    } catch (error) {
      if (!this.disposed && generation === this.snapshotValue.generation)
        this.update('reconnect', error instanceof TypeError ? 'network' : 'expired');
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  retry(): void {
    if (!readStored(this.sessionKey)) return;
    this.update('restoring');
    void this.restore();
  }

  reject(): void {
    this.access = null;
    this.update('reconnect', 'denied');
  }

  async logout(): Promise<void> {
    const token = readStored(this.sessionKey)?.refreshToken;
    this.access = null;
    this.sessionId = null;
    this.accountIntent = false;
    this.suppressed = true;
    this.pending = null;
    let stored = true;
    try {
      localStorage.setItem(this.suppressionKey, '1');
      localStorage.removeItem(this.sessionKey);
    } catch {
      stored = false;
    }
    this.update('guest', undefined, true);
    const generation = this.snapshotValue.generation;
    if (!stored) this.notice('storage-unavailable', generation);
    if (!token) return;
    try {
      const response = await fetch(new URL('/api/v1/helpdesk/sessions/logout', this.apiBase), {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: token }),
        signal: AbortSignal.timeout(10_000),
      });
      if (response.status !== 204) this.notice('revocation-unconfirmed', generation);
    } catch {
      this.notice('revocation-unconfirmed', generation);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.access = null;
    window.removeEventListener('storage', this.onStorage);
    this.listeners.clear();
  }
}
